import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { complianceAPI } from '../lib/api';

const inputStyle = {
  width: '100%', padding: '0.625rem 0.875rem', borderRadius: 10, border: '1px solid #CBD5E1',
  background: '#FAF7F2', color: '#002A38', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: '0.375rem' };
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const EMPTY = { auditor_name: '', audit_date: '', distribution_date: '', summary: '', source_url: '' };

export default function BiasAuditManager() {
  const [audits, setAudits] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    try {
      const res = await complianceAPI.listBiasAudits();
      setAudits(res.data.audits || []);
    } catch (e) {
      toast.error('Failed to load bias audits');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.auditor_name.trim()) { toast.error('Auditor name is required'); return; }
    if (!form.source_url.trim() && !file) { toast.error('Provide a public URL or upload a PDF'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('auditor_name', form.auditor_name.trim());
      if (form.audit_date) fd.append('audit_date', form.audit_date);
      if (form.distribution_date) fd.append('distribution_date', form.distribution_date);
      if (form.summary) fd.append('summary', form.summary);
      if (form.source_url) fd.append('source_url', form.source_url.trim());
      if (file) fd.append('file', file);
      await complianceAPI.createBiasAudit(fd);
      toast.success('Bias-audit summary published');
      setForm(EMPTY); setFile(null); setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not publish audit');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await complianceAPI.deleteBiasAudit(id);
      setAudits((prev) => prev.filter((a) => a.id !== id));
      toast.success('Audit removed');
    } catch (e) {
      toast.error('Could not remove audit');
    }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem', marginBottom: '1.5rem' }} data-testid="bias-audit-manager">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div>
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.0625rem', fontWeight: 700, color: '#0076A3', margin: 0 }}>Bias-Audit Summary (NYC LL144)</h2>
          <p style={{ fontSize: '0.8125rem', color: '#718096', marginTop: '0.25rem' }}>
            Publish the independent bias-audit summary. The latest one is shown publicly on the <a href="/ai-transparency" target="_blank" rel="noopener noreferrer" style={{ color: '#0076A3', fontWeight: 600 }}>AI Transparency page</a>.
          </p>
        </div>
        <Button data-testid="bias-audit-add-toggle" onClick={() => setOpen((o) => !o)} variant="outline" style={{ borderRadius: 10 }}>
          {open ? 'Cancel' : 'Publish New'}
        </Button>
      </div>

      {open && (
        <form onSubmit={submit} style={{ display: 'grid', gap: '0.875rem', borderTop: '1px solid #F0EBE3', paddingTop: '1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.875rem' }}>
            <div>
              <label style={labelStyle}>Independent auditor *</label>
              <input data-testid="bias-auditor-name" style={inputStyle} value={form.auditor_name} onChange={set('auditor_name')} placeholder="e.g. Acme Audit LLC" />
            </div>
            <div>
              <label style={labelStyle}>Audit date</label>
              <input type="date" data-testid="bias-audit-date" style={inputStyle} value={form.audit_date} onChange={set('audit_date')} />
            </div>
            <div>
              <label style={labelStyle}>Distribution date</label>
              <input type="date" data-testid="bias-distribution-date" style={inputStyle} value={form.distribution_date} onChange={set('distribution_date')} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Summary (optional)</label>
            <textarea data-testid="bias-summary" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} value={form.summary} onChange={set('summary')} placeholder="Brief summary of audit scope and results (selection/impact ratios)…" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem', alignItems: 'end' }}>
            <div>
              <label style={labelStyle}>Public URL to summary (optional)</label>
              <input data-testid="bias-source-url" style={inputStyle} value={form.source_url} onChange={set('source_url')} placeholder="https://…/bias-audit-summary" />
            </div>
            <div>
              <label style={labelStyle}>Or upload PDF (optional)</label>
              <input data-testid="bias-file-input" type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} style={{ ...inputStyle, padding: '0.5rem' }} />
            </div>
          </div>
          <div>
            <Button data-testid="bias-audit-submit" type="submit" disabled={saving} style={{ background: saving ? '#94A3B8' : '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
              {saving ? 'Publishing…' : 'Publish Summary'}
            </Button>
          </div>
        </form>
      )}

      {audits.length === 0 ? (
        <div style={{ padding: '1rem', textAlign: 'center', color: '#94A3B8', fontSize: '0.8125rem' }} data-testid="bias-audit-empty">No bias-audit summary published yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {audits.map((a, i) => (
            <div key={a.id} data-testid="bias-audit-row" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 0.875rem', borderRadius: 10, border: '1px solid #F0EBE3', background: i === 0 ? '#F0FDF4' : '#fff' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#0076A3' }}>
                  {a.auditor_name}
                  {i === 0 && <span style={{ marginLeft: '0.5rem', fontSize: '0.625rem', fontWeight: 700, color: '#059669', background: '#DCFCE7', padding: '0.1rem 0.4rem', borderRadius: 999 }}>LIVE</span>}
                </div>
                <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>
                  Audit {fmtDate(a.audit_date)} · Distributed {fmtDate(a.distribution_date)}
                </div>
              </div>
              {a.file_url && <a href={`${apiBase}${a.file_url}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: '#0076A3', fontWeight: 600 }}>PDF</a>}
              {a.source_url && <a href={a.source_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: '#0076A3', fontWeight: 600 }}>Link</a>}
              <button data-testid={`bias-audit-delete-${a.id}`} onClick={() => remove(a.id)} style={{ background: 'none', border: '1px solid #FECACA', color: '#DC2626', borderRadius: 8, padding: '0.25rem 0.5rem', fontSize: '0.6875rem', fontWeight: 600, cursor: 'pointer' }}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
